import { useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Star, ShieldCheck } from "lucide-react";
import { FaInstagram, FaTiktok } from "react-icons/fa6";
import { SITE, IMAGES } from "../../data/site";
import { scrollToId } from "../../lib/scroll";

const LINES = ["Votre permis,", "en toute", "confiance."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 4]);

  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rX = useSpring(tiltX, { stiffness: 140, damping: 16 });
  const rY = useSpring(tiltY, { stiffness: 140, damping: 16 });
  const [mag, setMag] = useState({ x: 0, y: 0 });

  const handleTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    tiltY.set(((e.clientX - r.left) / r.width - 0.5) * 9);
    tiltX.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
  };
  const resetTilt = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  return (
    <section ref={ref} id="accueil" data-testid="hero-section" className="relative overflow-hidden pt-[130px] pb-16 sm:pb-24">
      <div className="hero-grid absolute inset-0" aria-hidden="true" />
      <div className="glow-blob absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-violet-300/40" />
      <div className="glow-blob absolute top-24 right-[-180px] w-[560px] h-[560px] rounded-full bg-purple-200/50" />
      <div className="glow-blob absolute bottom-[-120px] left-1/3 w-[420px] h-[420px] rounded-full bg-indigo-200/40" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
        <div className="text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-4 py-1.5"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-violet-600 animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700">
              Auto-école à Chatou · Yvelines (78)
            </span>
          </motion.div>

          <h1 className="font-heading font-black tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-7">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-gradient" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="text-slate-600 leading-relaxed text-sm sm:text-base max-w-lg mx-auto lg:mx-0 mt-6"
            data-testid="hero-subtitle"
          >
            Bestconduite78, votre auto-école de proximité au cœur de Chatou. Code, permis B,
            conduite accompagnée : une pédagogie bienveillante, des moniteurs diplômés d'État
            et un suivi 100% personnalisé, de la première leçon au jour de l'examen.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="flex flex-wrap items-center justify-center lg:justify-start gap-4 mt-9"
          >
            <motion.span
              className="inline-block"
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setMag({ x: (e.clientX - r.left - r.width / 2) * 0.22, y: (e.clientY - r.top - r.height / 2) * 0.35 });
              }}
              onMouseLeave={() => setMag({ x: 0, y: 0 })}
              animate={{ x: mag.x, y: mag.y }}
              transition={{ type: "spring", stiffness: 160, damping: 14 }}
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollToId("#contact")}
                className="btn-shine group rounded-full bg-violet-700 text-white font-bold px-8 py-4 flex items-center gap-2.5 hover:bg-violet-800 hover:shadow-xl hover:shadow-violet-700/30 transition-[background-color,box-shadow] duration-200"
              >
                Réserver maintenant
                <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </motion.span>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("#formules")}
              className="rounded-full border-2 border-slate-200 text-slate-800 font-bold px-8 py-[14px] hover:border-violet-400 hover:text-violet-700 transition-colors duration-200"
            >
              Découvrir nos formules
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="flex items-center justify-center lg:justify-start gap-5 mt-10"
          >
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Suivez-nous</span>
            <div className="flex items-center gap-2.5">
              {[
                { icon: FaInstagram, href: SITE.socials.instagram, label: "Instagram", id: "instagram" },
                { icon: FaTiktok, href: SITE.socials.tiktok, label: "TikTok", id: "tiktok" },
              ].map(({ icon: Icon, href, label, id }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`hero-social-${id}`}
                  className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-white hover:bg-violet-700 hover:border-violet-700 hover:-translate-y-1 transition-[color,background-color,border-color,transform] duration-200"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
          style={{ perspective: 1100 }}
          onMouseMove={handleTilt}
          onMouseLeave={resetTilt}
        >
          <div className="glow-blob absolute inset-6 rounded-[3rem] bg-violet-500/35" />
          <motion.div style={{ y: yImg, rotate, rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }} className="relative">
            <div className="beam relative rounded-[2.2rem] w-full aspect-[4/3] bg-gradient-to-br from-violet-600 via-violet-800 to-slate-900 shadow-[0_30px_80px_rgb(109,40,217,0.28)] overflow-hidden flex items-center justify-center">
              <div className="scanline" aria-hidden="true" />
              <span className="hud-corner hud-corner-tl" aria-hidden="true" />
              <span className="hud-corner hud-corner-tr" aria-hidden="true" />
              <span className="hud-corner hud-corner-bl" aria-hidden="true" />
              <span className="hud-corner hud-corner-br" aria-hidden="true" />
              <div className="glow-blob absolute -top-12 -left-12 w-64 h-64 rounded-full bg-fuchsia-400/30" />
              <div className="glow-blob absolute -bottom-16 -right-10 w-72 h-72 rounded-full bg-violet-400/25" />
              <div className="road-strip" aria-hidden="true" />
              <div className="road-dashes" aria-hidden="true" />
              <span className="speed-line" style={{ top: "26%", animationDelay: "0s" }} aria-hidden="true" />
              <span className="speed-line" style={{ top: "38%", animationDelay: "0.5s", width: "140px" }} aria-hidden="true" />
              <span className="speed-line" style={{ top: "18%", animationDelay: "1s", width: "70px" }} aria-hidden="true" />
              <motion.div
                initial={{ x: 160, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-[92%]"
              >
                <motion.img
                  src={IMAGES.heroCar}
                  alt="Peugeot 208 grise de l'auto-école Bestconduite78, voiture de formation à Chatou (78)"
                  data-testid="hero-car-image"
                  loading="eager"
                  animate={{ y: [0, -7, 0], rotate: [0, -0.5, 0.4, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  className="relative w-full object-contain drop-shadow-[0_28px_38px_rgb(0,0,0,0.45)]"
                />
              </motion.div>
            </div>
            <motion.a
              href={SITE.googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              style={{ transform: "translateZ(50px)" }}
              className="absolute z-20 -bottom-6 -left-4 sm:-left-8"
              data-testid="hero-rating-card"
            >
              <div className="animate-float-slow bg-white/90 backdrop-blur-xl rounded-2xl border border-slate-100 shadow-[0_16px_50px_rgb(0,0,0,0.10)] px-5 py-4 flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Star size={20} className="text-violet-700 fill-violet-700" />
                </div>
                <div>
                  <p className="font-heading font-extrabold text-slate-900 text-lg leading-none">5/5</p>
                  <p className="text-xs text-slate-500 mt-1 font-semibold">Avis Google</p>
                </div>
              </div>
            </motion.a>
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.35 }}
              style={{ transform: "translateZ(50px)" }}
              className="absolute z-20 -top-5 right-6 bg-slate-900 text-white rounded-2xl px-5 py-3.5 flex items-center gap-3 shadow-xl"
              data-testid="hero-certif-card"
            >
              <ShieldCheck size={20} className="text-violet-300" />
              <div>
                <p className="text-sm font-bold leading-none">Moniteurs diplômés</p>
                <p className="text-[11px] text-slate-400 mt-1">Évaluation de départ : 55 €</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
